// @ts-nocheck
import * as React from "react";
import { useMatch } from "react-router-dom";
import {
  FileText, ClipboardList, Layers, Users, MapPin, CalendarClock, CalendarRange,
  Settings2, ClipboardCheck, Shield, IdCard, Mail, DollarSign, BedDouble, Boxes,
  ExternalLink, Printer, Trophy,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import type { ShellNavGroup } from "@/components/shell/AppShell";

export interface EventNavContext {
  eventId: string;
  name: string;
  startDate: string | null;
  endDate: string | null;
  status: string | null;
  schedulePublished: boolean;
  slug: string | null;
  groups: ShellNavGroup[];
}

const money = (n: number) => `$${Number(n ?? 0).toFixed(2)}`;

/**
 * Detects an open event (any /admin-dashboard/events/:eventId[/...] route) and
 * builds the event-scoped sidebar for it. Badge numbers reuse the same
 * `rle-operator-event-overview` payload the overview header already loads.
 */
export function useEventNav(isEnabled: (key: string) => boolean): EventNavContext | null {
  const match = useMatch("/admin-dashboard/events/:eventId/*");
  const indexMatch = useMatch("/admin-dashboard/events/:eventId");
  const raw = match?.params?.eventId ?? indexMatch?.params?.eventId ?? null;
  // Reserved non-UUID segments under /events (new, finance, faqs, analytics…)
  const eventId =
    raw && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)
      ? raw
      : null;

  const [info, setInfo] = React.useState<any>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!eventId) {
      setInfo(null);
      return;
    }
    (async () => {
      try {
        const [overviewRes, eventRow, courtRows] = await Promise.all([
          invokeEdgeFunction("rle-operator-event-overview", { body: { event_id: eventId } }),
          supabase.from("events").select("id, name, start_date, end_date, status, slug, published_schedule_version_id").eq("id", eventId).maybeSingle(),
          supabase.from("event_courts").select("id, venue_id, is_active").eq("event_id", eventId),
        ]);
        if (cancelled) return;
        const ov = overviewRes?.data?.ok ? overviewRes.data.data : null;
        const courts = (courtRows?.data || []).filter((c: any) => c.is_active !== false);
        setInfo({
          overview: ov,
          event: eventRow?.data ?? ov?.event ?? null,
          courts: courts.length,
          venues: new Set(courts.map((c: any) => c.venue_id).filter(Boolean)).size,
        });
      } catch {
        if (!cancelled) setInfo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  return React.useMemo(() => {
    if (!eventId) return null;
    const ev = info?.event ?? {};
    const ov = info?.overview ?? null;
    const base = `/admin-dashboard/events/${eventId}`;

    const regCount = ov?.registrations?.total ?? null;
    const pending = ov?.constraints?.by_status?.pending_review ?? 0;
    const outstanding = Number(ov?.financials?.total_outstanding ?? 0);

    // Tourney Machine's nine tabs, in TM's order and TM's words, with no
    // heading above them: General Info · Registration · Courts · Divisions ·
    // Scheduling · Post Scores · Send Notifications · Reports · Public Results.
    // Scheduling's own screens nest under it, the way TM nests Drag/Drop,
    // Batch and Save/Restore under its Scheduling tab.
    const tourneyMachine: ShellNavGroup = {
      items: [
        { label: "General Info", href: `${base}/edit`, icon: FileText },
        {
          label: "Registration",
          href: `${base}/registrations`,
          icon: ClipboardList,
          ...(regCount != null ? { badge: String(regCount) } : {}),
        },
        {
          label: "Courts",
          href: `${base}/venues-courts`,
          icon: MapPin,
          ...(info ? { badge: `${info.venues} · ${info.courts}` } : {}),
        },
        { label: "Divisions", href: `${base}/divisions`, icon: Layers },
        {
          label: "Scheduling",
          href: `${base}/scheduling`,
          icon: CalendarClock,
          children: [
            { label: "Pools", href: `${base}/scheduling/pools`, icon: Layers },
            { label: "Grid", href: `${base}/scheduling/grid`, icon: CalendarRange },
            { label: "Brackets", href: `${base}/scheduling/brackets`, icon: Trophy },
            { label: "Bulk edit", href: `${base}/scheduling/bulk-edit`, icon: Layers },
            { label: "Team summary", href: `${base}/scheduling/team-summary`, icon: Users },
            {
              label: "Schedule Requests",
              href: `${base}/scheduling-requests`,
              icon: CalendarClock,
              ...(ov
                ? pending > 0
                  ? { badge: String(pending), badgeTone: "amber" as const }
                  : { badge: "0" }
                : {}),
            },
            { label: "Schedule setup", href: `${base}/schedule-setup`, icon: Settings2 },
            { label: "Print pack", href: `${base}/print-pack`, icon: Printer },
          ],
        },
        { label: "Post Scores", href: `${base}/post-scores`, icon: ClipboardCheck },
        { label: "Send Notifications", href: `${base}/messages`, icon: Mail },
        // No "Reports" here: it pointed at the organization-level analytics page,
        // which leaves the event entirely. Team summary and Print pack live under
        // Scheduling, where their data is.
        ...(ev?.slug
          ? [{
              label: "Public Results",
              href: `/events/${ev.slug}`,
              icon: ExternalLink,
              external: true,
            }]
          : []),
      ],
    };

    // Everything that is ours and not TM's lives below, under one heading.
    const ours: ShellNavGroup = {
      label: "More tools",
      items: [
        ...(isEnabled("player_verification")
          ? [{ label: "Rosters", href: `${base}/verification`, icon: Users }]
          : []),
        { label: "Check-in", href: `/admin/events/${eventId}/checkin`, icon: ClipboardCheck },
        ...(isEnabled("officials")
          ? [{ label: "Officials", href: `${base}/officials`, icon: Shield }]
          : []),
        { label: "Credentials", href: `${base}/credentials-coaches`, icon: IdCard },
        ...(isEnabled("event_ops")
          ? [{ label: "Event Management", href: `${base}/event-ops`, icon: Boxes }]
          : []),
        {
          label: "Money",
          href: `${base}/finance`,
          icon: DollarSign,
          ...(outstanding > 0
            ? { badge: money(outstanding), badgeTone: "amber" as const }
            : {}),
        },
        ...(isEnabled("hotels")
          ? [{ label: "Hotels", href: `${base}/hotels`, icon: BedDouble }]
          : []),
      ],
    };

    return {
      eventId,
      name: ev?.name ?? "Event",
      startDate: ev?.start_date ?? null,
      endDate: ev?.end_date ?? null,
      status: ev?.status ?? null,
      schedulePublished: !!ev?.published_schedule_version_id,
      slug: ev?.slug ?? null,
      groups: [tourneyMachine, ours],
    };
  }, [eventId, info, isEnabled]);
}
